import React, { useMemo } from "react"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { WithClassName } from "shared/types/WithClassName"
import { FormProvider as BaseFormProvider } from "shared/ui/form/FormContext"

import { FloorFormModel } from "features/floor-management/types/FloorFormModel"

import { FloorFormAttachmentsProvider } from "./context/FloorFormAttachmentsContext"
import { FloorFormProvider } from "./contexts/FloorFormContext"
import { useFloorFormViewModel } from "./model/useFloorFormViewModel"
import "./styles/_floor-form.scss"
import { FloorFormClarificationRequest } from "./ui/FloorFormClarificationRequest"
import { FloorFormRemarkTab } from "./ui/FloorFormRemarkTab"
import { FloorFormTabs } from "./ui/FloorFormTabs"
import { FloorLabelInput } from "./ui/inputs/FloorLabelInput"
import { FloorPositionInput } from "./ui/inputs/FloorPositionInput"
import { FloorProjectInput } from "./ui/inputs/FloorProjectInput"
import { FloorRoomAssignmentInput } from "./ui/inputs/FloorRoomAssignmentInput"

export interface FloorFormProps extends FloorFormModel, WithClassName {
  id: string
  autoFocus?: boolean
  isReadonly?: boolean
  isDisabled?: boolean
  children?: React.ReactNode
}

const FloorFormRoot = React.memo((props: FloorFormProps) => {
  const {
    id,
    className,
    children,
    isReadonly,
    isDisabled,
    positionOptions,
    isLoadingPositionOptions,
    roomOptions,
    isLoadingRoomOptions,
    projectOptions,
    isLoadingProjectOptions,
    formOptions,
    model
  } = props

  const {
    hideProject = true,
    hideRemark,
    hideClarificationRequest,
    hideAttachments
  } = formOptions || {}

  const { t } = useTranslation()
  const { form, onSubmit, attachmentsContext } = useFloorFormViewModel(props)

  const contextValue = useMemo(
    () => ({
      isReadonly,
      isDisabled,
      positionOptions,
      isLoadingPositionOptions,
      roomOptions,
      isLoadingRoomOptions,
      projectOptions,
      isLoadingProjectOptions,
      hideProject,
      hideRemark,
      hideClarificationRequest,
      hideAttachments,
      model
    }),
    [
      isReadonly,
      isDisabled,
      positionOptions,
      isLoadingPositionOptions,
      roomOptions,
      isLoadingRoomOptions,
      projectOptions,
      isLoadingProjectOptions,
      hideProject,
      hideRemark,
      hideClarificationRequest,
      hideAttachments,
      model
    ]
  )

  return (
    <BaseFormProvider isReadonly={isReadonly} isDisabled={isDisabled}>
      <FloorFormProvider value={contextValue}>
        <FloorFormAttachmentsProvider value={attachmentsContext}>
          <FormProvider {...form}>
            <form
              id={id}
              onSubmit={onSubmit}
              aria-label={t("Floor.Actions.Create")}
              className={classNames("form floor-form", className)}
              data-testid="floor-form"
            >
              {children || (
                <>
                  <FloorLabelInput autoFocus />
                  <FloorPositionInput />
                  <FloorRoomAssignmentInput />
                </>
              )}
            </form>
          </FormProvider>
        </FloorFormAttachmentsProvider>
      </FloorFormProvider>
    </BaseFormProvider>
  )
})

export const FloorForm = Object.assign(FloorFormRoot, {
  LabelInput: FloorLabelInput,
  PositionInput: FloorPositionInput,
  ProjectInput: FloorProjectInput,
  RoomAssignmentInput: FloorRoomAssignmentInput,
  ClarificationRequest: FloorFormClarificationRequest,
  RemarkTab: FloorFormRemarkTab,
  Tabs: FloorFormTabs
})
